import React, { useState, useRef, useCallback, useEffect } from "react";
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Typography,
    Box,
    Stack,
} from "@mui/material";
import { Engineering as TravelerIcon } from "@mui/icons-material";
import WorkOrderService from "../../services/WorkOrder.service";

// Only steps in these statuses can accept more logged time.
const SELECTABLE_STATUSES = ["pending", "in_progress", "submitted"];

/**
 * Two-step autocomplete: search/select a Work Order by number, then pick a
 * specific traveler (operation step) within it. Used by the Timesheet dialog
 * to log payroll hours against a WO operation instead of (or alongside) a
 * project.
 *
 * Props:
 *   value      - controlled selected traveler object (with an embedded work_order) or null
 *   onChange   - called with the selected traveler object or null
 *   label      - optional field label for the WO search field (default: "Work Order")
 *   disabled   - disables both inputs
 *   error      - error message string, shown under the traveler field
 */
export default function WorkOrderTravelerPicker({
    value,
    onChange,
    label = "Work Order",
    disabled = false,
    error = "",
}) {
    const [workOrder, setWorkOrder] = useState(
        value?.work_order ? value.work_order : null,
    );
    const [woOptions, setWoOptions] = useState([]);
    const [woLoading, setWoLoading] = useState(false);
    const woTimerRef = useRef(null);

    const [travelerOptions, setTravelerOptions] = useState([]);
    const [travelerLoading, setTravelerLoading] = useState(false);

    useEffect(() => {
        if (!workOrder) {
            setTravelerOptions([]);
            return;
        }
        setTravelerLoading(true);
        WorkOrderService.listTravelers(workOrder.id)
            .then((res) => {
                const results = Array.isArray(res.data)
                    ? res.data
                    : (res.data?.results ?? []);
                setTravelerOptions(
                    results.filter((t) => SELECTABLE_STATUSES.includes(t.status)),
                );
            })
            .catch(() => setTravelerOptions([]))
            .finally(() => setTravelerLoading(false));
    }, [workOrder]);

    const handleWoInputChange = useCallback((_, inputVal, reason) => {
        clearTimeout(woTimerRef.current);

        if (reason !== "input") {
            setWoLoading(false);
            return;
        }

        const term = (inputVal || "").trim();
        setWoOptions([]);

        if (term.length < 2) {
            setWoLoading(false);
            return;
        }

        setWoLoading(true);
        woTimerRef.current = setTimeout(async () => {
            try {
                const res = await WorkOrderService.listWorkOrders({
                    search: term,
                    status: "released,in_progress",
                    page_size: 20,
                });
                const results = Array.isArray(res.data)
                    ? res.data
                    : (res.data?.results ?? []);
                setWoOptions(results);
            } catch {
                setWoOptions([]);
            } finally {
                setWoLoading(false);
            }
        }, 250);
    }, []);

    const handleWoChange = (newWo) => {
        setWorkOrder(newWo);
        onChange(null);
    };

    const handleTravelerChange = (traveler) => {
        onChange(traveler ? { ...traveler, work_order: workOrder } : null);
    };

    const getWoLabel = (opt) => {
        if (!opt) return "";
        const partDescription = opt.part_description || opt.part_detail?.description || "";
        return `${opt.wo_number}${partDescription ? ` — ${partDescription}` : ""}`.trim();
    };

    const getTravelerLabel = (opt) => {
        if (!opt) return "";
        return `${opt.traveler_number || `Step ${opt.step_number}`} — ${opt.step_name} (${opt.status})`;
    };

    return (
        <Stack spacing={2}>
            <Autocomplete
                options={woOptions}
                value={workOrder}
                onChange={(_, newVal) => handleWoChange(newVal)}
                onInputChange={handleWoInputChange}
                filterOptions={(x) => x}
                getOptionLabel={getWoLabel}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                loading={woLoading}
                disabled={disabled}
                noOptionsText="Type at least 2 characters to search…"
                renderOption={({ key, ...optProps }, opt) => (
                    <Box
                        key={key}
                        component="li"
                        {...optProps}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <TravelerIcon fontSize="small" sx={{ color: "text.disabled", flexShrink: 0 }} />
                        <Typography variant="body2">{getWoLabel(opt)}</Typography>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        size="small"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {woLoading && <CircularProgress size={18} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            <Autocomplete
                options={travelerOptions}
                value={value || null}
                onChange={(_, newVal) => handleTravelerChange(newVal)}
                getOptionLabel={getTravelerLabel}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                loading={travelerLoading}
                disabled={disabled || !workOrder}
                noOptionsText={
                    workOrder ? "No open steps on this work order" : "Pick a work order first"
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Operation Step"
                        size="small"
                        error={Boolean(error)}
                        helperText={error || ""}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {travelerLoading && <CircularProgress size={18} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </Stack>
    );
}
